import { motion } from "framer-motion";
import { Check } from "lucide-react";

const SPECS = [
  ["Year", "1987"],
  ["Model", "Vanagon Syncro — Type 2 (T3)"],
  ["Body", "Tin top, solid steel roof"],
  ["Engine", "2.1L Waterboxer flat-four, petrol"],
  ["Gearbox", "4-speed manual + ultra-low granny first"],
  ["Drive", "Full-time Syncro 4WD, viscous coupling + decoupler"],
  ["Diff lock", "Rear axle"],
  ["Suspension", "Lifted, sway bars, BMS bushings"],
  ["Tires", "Michelin Defender"],
  ["Lighting", "South African headlight kit"],
  ["Mileage", "224,000 miles"],
  ["Price", "$25,000"],
];

const UPGRADES = [
  "Upgraded LED interior & exterior lights",
  "Pioneer Bluetooth touchscreen head unit",
  "Kicker powered subwoofer",
  "Color backup camera",
  "GPS speedometer kit",
  "Passenger swivel seat",
  "Custom-built center console",
  "Spring-loaded cup holders",
  "Big Shot shifter",
  "ABS front door panel set",
  "Dash cover & Covercraft UVS100 sunscreen",
  "Limo tinting on all windows",
  "Dynamat sound dampening throughout",
  "Stainless steel coolant transfer pipes",
  "Audible coolant temp alarm",
  "Auxiliary battery",
  "Stainless performance exhaust — CARB 50-state",
  "GoWesty plate steel bumper set",
  "Dual-nozzle windshield washer jets",
  "Heavy-duty high-offset window cranks",
  "BMS upper control arm upgrade",
  "Spare tire steering shaft protector",
  "Oversized spare tire carrier",
  "Mechanically sorted, turn-key",
  "Heavy-duty roof racks — rated for real loads (optional)",
  "Yakima Mega-Warrior basket (optional)",
  "Custom hatch-mounted bike rack (optional)",
  "Rooftop tent — triple berth (optional)",
];

export default function BuildSheet() {
  return (
    <section id="build-sheet" className="py-24 md:py-40 border-t border-border" data-testid="build-sheet-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-4"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4">Document 87-A</p>
            <h2 className="font-headings uppercase text-5xl md:text-6xl leading-[0.9] mb-6">
              Build<br />Sheet
            </h2>
            <p className="font-editorial italic text-xl md:text-2xl text-secondary leading-snug mb-6">
              “Read it like a workshop manual. Every line earns its place.”
            </p>
            <p className="text-base leading-relaxed text-foreground/70">
              Full history, invoices and inspection reports are available to serious
              enquirers. Viewings by appointment.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-8"
          >
            <div className="border border-border" data-testid="spec-table">
              <div className="flex justify-between items-center px-5 md:px-8 py-4 border-b border-border bg-muted">
                <span className="font-mono text-xs uppercase tracking-[0.3em]">Specification</span>
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">SYNCRO/87</span>
              </div>
              {SPECS.map(([k, v], i) => (
                <div
                  key={k}
                  className={`flex items-baseline gap-3 px-5 md:px-8 py-3.5 md:py-4 ${i !== SPECS.length - 1 ? "border-b border-border" : ""} hover:bg-muted/60 transition-colors duration-300`}
                  data-testid={`spec-row-${i}`}
                >
                  <span className="font-mono text-xs md:text-sm uppercase tracking-widest text-muted-foreground shrink-0">{k}</span>
                  <span className="flex-1 border-b border-dotted border-accent translate-y-[-4px]" />
                  <span className="font-mono text-xs md:text-sm uppercase tracking-widest text-right">{v}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4" data-testid="upgrades-list">
              {UPGRADES.map((u, i) => (
                <motion.div
                  key={u}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="flex items-center gap-3 font-mono text-xs md:text-sm uppercase tracking-widest"
                  data-testid={`upgrade-${i}`}
                >
                  <span className="w-6 h-6 border border-primary flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-primary" />
                  </span>
                  {u}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
